import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PollListItem } from "@/components/PollListItem.tsx";
import { Poll, PollStatus } from "@/types/Poll.ts";
import { useState, useEffect } from "react";
import { Users } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import PollCard from "@/components/PollCard";
import {Session} from "@supabase/supabase-js";
import {useAuth} from "@/contexts/AuthContext.tsx";

export const FeaturedPolls = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [userCount, setUserCount] = useState(230119);
  const ITEMS_PER_PAGE = 20;
  /*const [userSession] = useState<Session | null>(() => {
    const cachedSession = localStorage.getItem('session');
    return cachedSession ? JSON.parse(cachedSession) : null;
  });*/
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const {user, session, loading: authLoading} = useAuth();

  useEffect(() => {
    const savedCount = localStorage.getItem('userCount');
    if (savedCount) {
      setUserCount(parseInt(savedCount));
    } else {
      localStorage.setItem('userCount', userCount.toString());
    }

    const countIncreaseInterval = setInterval(() => {
      const randomIncrease = Math.floor(Math.random() * 10) + 1;
      setUserCount(prevCount => {
        const newCount = prevCount + randomIncrease;
        localStorage.setItem('userCount', newCount.toString());
        return newCount;
      });
    }, Math.floor(Math.random() * (26000 - 13000 + 1) + 13000));

    return () => {
      clearInterval(countIncreaseInterval);
    };
  }, []);

  const { data: polls, isLoading, refetch } = useQuery({
    queryKey: ['polls',session],
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${session?.access_token}`
      }
      const response = await fetch(`${import.meta.env.VITE_API_URL}/polls?order=createdAt&isFeatured=true`, {
        method: 'GET',
        headers: session?.access_token ? headers : {},
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: Poll[] = await response.json();
      return data;
    }
  });

  const displayedPolls = polls?.filter((x) => !x.isTop).slice(0, page * ITEMS_PER_PAGE);
  const hasMore = polls ? displayedPolls.length < polls.length : false;
  const isLoadingMore = isLoading && page > 1;

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Featured polls for the carousel - select 3 polls for demonstration
  const featuredCarouselPolls = polls?.filter((x) => x.isTop == true) || [];

  // Dot navigation handler
  const handleDotClick = (idx: number) => {
    if (carouselApi) {
      carouselApi.scrollTo(idx);
    }
  };
  // Listen to carousel select event
  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setSelectedIndex(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    onSelect();
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  // Auto-slide every 10 seconds
  /*useEffect(() => {
    if (!carouselApi || featuredCarouselPolls.length <= 1) return;
    const interval = setInterval(() => {
      const nextIndex = (carouselApi.selectedScrollSnap() + 1) % featuredCarouselPolls.length;
      carouselApi.scrollTo(nextIndex);
    }, 10000);
    return () => clearInterval(interval);
  }, [carouselApi, featuredCarouselPolls.length]);*/

  return (
    <>
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          </div>
        </div>
      ) : (
        <>
          {featuredCarouselPolls?.length > 0 && (
              <div className="max-w-7xl mx-auto px-4 mb-8">
                <Carousel className="w-full" setApi={setCarouselApi}>
                  <CarouselContent>
                    {featuredCarouselPolls.map((poll) => (
                        <CarouselItem key={poll.id}>
                          <div className="relative">
                            <PollCard poll={poll} />
                            {/* Dot navigation inside card, bottom center */}
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-3 hidden sm:flex gap-2 z-10">
                              {featuredCarouselPolls.map((_, idx) => (
                                  <button
                                      key={idx}
                                      onClick={() => handleDotClick(idx)}
                                      className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 focus:outline-none
                              ${selectedIndex === idx
                                          ? 'bg-[#155231] border-[#155231]'
                                          : 'bg-gray-300 border border-gray-300 dark:bg-gray-600 dark:border-gray-600'}
                            `}
                                      aria-label={`Go to slide ${idx + 1}`}
                                  />
                              ))}
                            </div>
                          </div>
                        </CarouselItem>
                    ))}
                    {featuredCarouselPolls.length === 0 && (
                        <CarouselItem>
                          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                        </CarouselItem>
                    )}
                  </CarouselContent>
                </Carousel>
              </div>
          )}

          {/* Regular Polls Grid */}
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {displayedPolls?.map((poll) => (
                <PollListItem refetch={refetch} poll={poll} key={poll.id} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={handleLoadMore}
                  className="bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
